/**
 * PianoSviluppoPdfModal.tsx
 * 
 * Modale per la compilazione e generazione del PDF Piano di Sviluppo
 */

import { useEffect, useRef } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Grid,
  Title,
  Divider,
  LoadingOverlay,
  Select,
} from '@mantine/core';
import { SignatureInput } from './SignatureInput';
import { useForm } from '@mantine/form';
import { DateInput } from '@mantine/dates';
import { IconDownload, IconFileTypePdf } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { pianoSviluppoPdfService, type GeneratePianoSviluppoPdfRequest } from '../../services/piano-sviluppo-pdf-service';
import { RequestType, type Request } from '../../types/api';
import SignatureCanvas from 'react-signature-canvas';

interface PianoSviluppoPdfModalProps {
  opened: boolean;
  onClose: () => void;
  request: Request;
}

export function PianoSviluppoPdfModal({ opened, onClose, request }: PianoSviluppoPdfModalProps) {

  // Mutation per generare il PDF
  const generatePdfMutation = useMutation({
    mutationFn: (data: GeneratePianoSviluppoPdfRequest) => 
      pianoSviluppoPdfService.generateAndDownloadPdf(data),
    onSuccess: () => {
      notifications.show({
        title: 'PDF Generato',
        message: 'Il PDF è stato generato e scaricato con successo',
        color: 'green',
      });
      onClose();
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Errore durante la generazione del PDF',
        color: 'red',
      });
    },
  });

  const form = useForm({
    initialValues: {
      nomeProgetto: '',
      numeroProgressivo: '',
      data: new Date(),
      formaBianco: '',
      
      // Bozzetto
      bozzettoRiassumeVerificaTecnicaElementiIngresso: '',
      bozzettoSviluppoBozzetto: '',
      bozzettoRiassumeVerificaBozzettoData: '',
      bozzettoRiassumeVerificaBozzettoFirma: '',
      bozzettoRiferimentoProgetto: '',
      bozzettoNumeroTavole: '',
      bozzettoStatoProgetto: '',
      
      // Campione
      campioneRiassumeVerificaTecnicaElementiIngresso: '',
      campioneSviluppoCampioneImpiantiColoriSpecificheTecniche: '',
      campioneRiassumeVerificaCampioneData: '',
      campioneRiassumeVerificaCampioneFirma: '',
      campioneRiferimentoProgetto: '',
      campioneCodiceDecoroOdv: '',
      campioneStatoProgetto: '',
      
      // Personalizzato Produzione
      produzioneApprovazioneBozzettoCampione: '',
      produzioneSviluppoImpiantiPellicole: '',
      produzioneRiassumeVerificaSviluppoDecoroData: '',
      produzioneRiassumeVerificaSviluppoDecoroFirma: '',
      produzioneRiferimentoProgetto: '',
      produzioneCodiceDecoroOdv: '',
      produzioneStatoProgetto: '',
      
      // Note
      notes: '',

      // Qualità (solo Produzione)
      qualita: '',

      // Test Conformità
      testConformitaData: '',
      testConformitaFirma: '',
      
      // Validazione Progetto
      validazioneData: '',
      validazioneFirma: '',
    },
    validate: {
      nomeProgetto: (value: string) => (value.length < 3 ? 'Nome progetto obbligatorio' : null),
      numeroProgressivo: (value: string) => (value.length === 0 ? 'Numero progressivo obbligatorio' : null),
    },
  });

  // Popola i valori iniziali quando il modale si apre
  // Usa direttamente il numero progressivo dalla richiesta invece di chiamare l'API
  useEffect(() => {
    if (opened && request) {
      // Costruisce il numero progressivo formattato dai campi della richiesta
      const formattedProgressiveNumber = request.progressiveNumberFormatted
        || (request.progressiveNumber && request.progressiveYear
            ? `${request.progressiveNumber}/${request.progressiveYear}`
            : '');

      form.setValues({
        ...form.values,
        nomeProgetto: request.title || '',
        numeroProgressivo: formattedProgressiveNumber,
        data: request.createdAt ? new Date(request.createdAt) : new Date(),
        formaBianco: '', // Campo opzionale, lasciato vuoto per default
        notes: request.notes || '',
      });
    }
  }, [opened, request]);

  const signatureRef = useRef<SignatureCanvas | null>(null);

  const handleClearSignature = () => {
    signatureRef.current?.clear();
  };

  const handleSubmit = form.onSubmit((values) => {
    const signatureData = signatureRef.current?.toDataURL(); // Firma come base64

    const pdfRequest: GeneratePianoSviluppoPdfRequest = {
      requestId: parseInt(request.id),
      nomeProgetto: values.nomeProgetto,
      numeroProgressivo: values.numeroProgressivo,
      data: values.data.toLocaleDateString('it-IT'),
      formaBianco: values.formaBianco,
      requestType: request.requestType,
      notes: values.notes || undefined,
      qualita: request.requestType.toString() === 'Produzione' ? values.qualita || undefined : undefined,
      testConformitaSection: {
        data: values.testConformitaData,
        firma: signatureData || '', // Firma disegnata
      },
      validazioneProgettoSection: {
        data: values.validazioneData,
        firma: values.validazioneFirma,
      },
    };

    // Aggiungi la sezione specifica in base al tipo di richiesta
    switch (request.requestType.toString()) {
      case 'Bozzetto':
        pdfRequest.bozzettoSection = {
          riassumeVerificaTecnicaElementiIngresso: values.bozzettoRiassumeVerificaTecnicaElementiIngresso,
          sviluppoBozzetto: values.bozzettoSviluppoBozzetto,
          riassumeVerificaBozzettoData: values.bozzettoRiassumeVerificaBozzettoData,
          riassumeVerificaBozzettoFirma: values.bozzettoRiassumeVerificaBozzettoFirma,
          riferimentoProgetto: values.bozzettoRiferimentoProgetto,
          numeroTavole: values.bozzettoNumeroTavole,
          statoProgetto: values.bozzettoStatoProgetto,
        };
        break;
      case 'Campionatura':
        pdfRequest.campioneSection = {
          riassumeVerificaTecnicaElementiIngresso: values.campioneRiassumeVerificaTecnicaElementiIngresso,
          sviluppoCampioneImpiantiColoriSpecificheTecniche: values.campioneSviluppoCampioneImpiantiColoriSpecificheTecniche,
          riassumeVerificaCampioneData: values.campioneRiassumeVerificaCampioneData,
          riassumeVerificaCampioneFirma: values.campioneRiassumeVerificaCampioneFirma,
          riferimentoProgetto: values.campioneRiferimentoProgetto,
          codiceDecoroOdv: values.campioneCodiceDecoroOdv,
          statoProgetto: values.campioneStatoProgetto,
        };
        break;
      case 'Produzione':
        pdfRequest.personalizzatoProduzioneSection = {
          approvazioneBozzettoCampione: values.produzioneApprovazioneBozzettoCampione,
          sviluppoImpiantiPellicole: values.produzioneSviluppoImpiantiPellicole,
          riassumeVerificaSviluppoDecoroData: values.produzioneRiassumeVerificaSviluppoDecoroData,
          riassumeVerificaSviluppoDecoroFirma: values.produzioneRiassumeVerificaSviluppoDecoroFirma,
          riferimentoProgetto: values.produzioneRiferimentoProgetto,
          codiceDecoroOdv: values.produzioneCodiceDecoroOdv,
          statoProgetto: values.produzioneStatoProgetto,
        };
        break;
    }

    generatePdfMutation.mutate(pdfRequest);
  });
  const renderBozzettoSection = () => (
    <>
      <Title order={4} mt="md" mb="sm">Sezione Bozzetto</Title>
      <Grid>
        <Grid.Col span={12}>
          <Textarea
            label="Riesame/Verifica Tecnica Elementi in Ingresso"
            placeholder="Inserisci riesame/verifica tecnica elementi in ingresso"
            {...form.getInputProps('bozzettoRiassumeVerificaTecnicaElementiIngresso')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label="Sviluppo Bozzetto"
            placeholder="Inserisci sviluppo bozzetto"
            {...form.getInputProps('bozzettoSviluppoBozzetto')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Riesame/Verifica Bozzetto - Data"
            placeholder="gg/mm/aaaa"
            {...form.getInputProps('bozzettoRiassumeVerificaBozzettoData')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <SignatureInput
            label="Riesame/Verifica Bozzetto - Firma"
            {...form.getInputProps('bozzettoRiassumeVerificaBozzettoFirma')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Riferimento Progetto"
            placeholder="Es: 01/01/0001"
            {...form.getInputProps('bozzettoRiferimentoProgetto')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="N° Tavole"
            placeholder="Numero tavole"
            {...form.getInputProps('bozzettoNumeroTavole')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Stato Progetto"
            placeholder="Es: In Progress"
            {...form.getInputProps('bozzettoStatoProgetto')}
          />
        </Grid.Col>
      </Grid>
    </>
  );

  const renderCampioneSection = () => (
    <>
      <Title order={4} mt="md" mb="sm">Sezione Campione</Title>
      <Grid>
        <Grid.Col span={12}>
          <Textarea
            label="Riesame/Verifica Tecnica Elementi in Ingresso"
            placeholder="Inserisci riesame/verifica tecnica elementi in ingresso"
            {...form.getInputProps('campioneRiassumeVerificaTecnicaElementiIngresso')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label="Sviluppo Campione (Impianti / Colori / Specifiche Tecniche)"
            placeholder="Inserisci sviluppo campione con impianti, colori e specifiche tecniche"
            {...form.getInputProps('campioneSviluppoCampioneImpiantiColoriSpecificheTecniche')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Riesame/Verifica Campione - Data"
            placeholder="gg/mm/aaaa"
            {...form.getInputProps('campioneRiassumeVerificaCampioneData')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Riesame/Verifica Campione - Firma"
            placeholder="Nome firma"
            {...form.getInputProps('campioneRiassumeVerificaCampioneFirma')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Riferimento Progetto"
            placeholder="Es: 01/01/0001"
            {...form.getInputProps('campioneRiferimentoProgetto')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Codice Decoro / ODV"
            placeholder="Inserisci codice"
            {...form.getInputProps('campioneCodiceDecoroOdv')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Stato Progetto"
            placeholder="Es: In Progress"
            {...form.getInputProps('campioneStatoProgetto')}
          />
        </Grid.Col>
      </Grid>
    </>
  );

  const renderProduzioneSection = () => (
    <>
      <Title order={4} mt="md" mb="sm">Sezione Personalizzato Produzione</Title>
      <Grid>
        <Grid.Col span={12}>
          <Textarea
            label="Approvazione Bozzetto/Campione"
            placeholder="Inserisci approvazione"
            {...form.getInputProps('produzioneApprovazioneBozzettoCampione')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label="Sviluppo Impianti/Pellicole"
            placeholder="Inserisci sviluppo impianti"
            {...form.getInputProps('produzioneSviluppoImpiantiPellicole')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Riesame/Verifica Sviluppo Decoro - Data"
            placeholder="gg/mm/aaaa"
            {...form.getInputProps('produzioneRiassumeVerificaSviluppoDecoroData')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Riesame/Verifica Sviluppo Decoro - Firma"
            placeholder="Nome firma"
            {...form.getInputProps('produzioneRiassumeVerificaSviluppoDecoroFirma')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Riferimento Progetto"
            placeholder="Es: 01/01/0001"
            {...form.getInputProps('produzioneRiferimentoProgetto')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Codice Decoro / ODV"
            placeholder="Inserisci codice"
            {...form.getInputProps('produzioneCodiceDecoroOdv')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label="Stato Progetto"
            placeholder="Es: In Progress"
            {...form.getInputProps('produzioneStatoProgetto')}
          />
        </Grid.Col>
      </Grid>
    </>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <IconFileTypePdf size={24} />
          <span>Genera Piano di Sviluppo PDF</span>
        </Group>
      }
      size="xl"
      styles={{ 
        body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' },
        content: { maxWidth: '90vw' }
      }}
      scrollAreaComponent={undefined}
    >
      <LoadingOverlay visible={generatePdfMutation.isPending} />
      
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {/* Informazioni Generali */}
          <Title order={4}>Informazioni Generali</Title>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Nome Progetto"
                placeholder="Nome del progetto"
                required
                {...form.getInputProps('nomeProgetto')}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput
                label="Progetto n°"
                placeholder="1/2025"
                required
                description="Numero modificabile"
                {...form.getInputProps('numeroProgressivo')}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <DateInput
                label="Data Inserimento"
                placeholder="Seleziona data"
                required
                valueFormat="DD/MM/YYYY"
                {...form.getInputProps('data')}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <TextInput
                label="Forma di Bianco"
                placeholder="Forma di bianco"
                {...form.getInputProps('formaBianco')}
              />
            </Grid.Col>
          </Grid>

          <Divider />

          {/* Sezione dinamica in base al tipo di richiesta */}
          {request.requestType.toString() === 'Bozzetto' && renderBozzettoSection()}
          {request.requestType.toString() === 'Campionatura' && renderCampioneSection()}
          {request.requestType.toString() === 'Produzione' && renderProduzioneSection()}
          {request.requestType.toString() === 'Gamma' && renderBozzettoSection()}

          <Divider />

          {/* Note */}
          <Title order={4}>Note</Title>
          <Textarea
            label="Note"
            placeholder="Inserisci eventuali note..."
            minRows={3}
            autosize
            {...form.getInputProps('notes')}
          />

          {request.requestType.toString() === 'Produzione' && (
            <>
              <Divider />
              <Title order={4}>Qualità</Title>
              <Select
                label="Qualità"
                placeholder="Seleziona una voce..."
                data={[
                  'Analisi non necessarie-decoro sotto smalto',
                  'Analisi non necessarie-decoro già testato',
                  'Analisi non necessarie-oggettistica',
                  'Analisi non necessarie-altro',
                ]}
                clearable
                {...form.getInputProps('qualita')}
              />
            </>
          )}

          <Divider />

          {/* Test Conformità a Norme */}
          <Title order={4}>Test Conformità a Norme</Title>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Data"
                placeholder="gg/mm/aaaa"
                {...form.getInputProps('testConformitaData')}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <SignatureInput
                label="Firma"
                {...form.getInputProps('testConformitaFirma')}
              />
            </Grid.Col>
          </Grid>

          <Divider />

          {/* Validazione Progetto */}
          <Title order={4}>Validazione Progetto</Title>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Data"
                placeholder="gg/mm/aaaa"
                {...form.getInputProps('validazioneData')}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <SignatureInput
                label="Firma"
                {...form.getInputProps('validazioneFirma')}
              />
            </Grid.Col>
          </Grid>

          {/* Firma disegnata */}
          <Divider my="sm" label="Firma" />
          <Stack>
            <SignatureCanvas
              ref={signatureRef}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'signature-canvas' }}
            />
            <Group justify="apart" mt="xs">
              <Button variant="outline" color="red" onClick={handleClearSignature}>
                Cancella Firma
              </Button>
            </Group>
          </Stack>

          {/* Pulsanti azione */}
          <Group justify="flex-end" mt="xl">
            <Button variant="subtle" onClick={onClose}>
              Annulla
            </Button>
            <Button
              type="submit"
              leftSection={<IconDownload size={16} />}
              loading={generatePdfMutation.isPending}
            >
              Genera e Scarica PDF
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
